// ticket-dag-stall-explainer.ts
//
// Phase 03 (R-15, Req 3): human-readable reasons for a `blocked` outcome that
// is a genuinely stalled DAG (no ticket ready, DAG not exhausted) — not
// `validateTicketDag` (ticket-dag-validator.ts), which REJECTS at `init`/plan
// time and stops at the first violation. This runs AFTER dispatch already
// stalled, so it lists everything it can find (capped at 5 lines) instead of
// stopping early. Own DFS 3-color cycle walk (does not call
// validateTicketDag) — see Architecture note in phase-03 plan for why.

import type { TicketRuntimeStatus } from "./scheduler.js";
import type { Ticket } from "./schemas/ticket-schema.js";

const MAX_LINES = 5;

function statusOf(statusMap: Record<string, TicketRuntimeStatus>, id: string): TicketRuntimeStatus {
  return statusMap[id] ?? "todo";
}

/** Every non-done ticket blocked on an unmet/failed/blocked/unknown dependency. */
function findBlockedByDependency(
  tickets: Ticket[],
  statusMap: Record<string, TicketRuntimeStatus>,
): string[] {
  const known = new Set(tickets.map((t) => t.id));
  const lines: string[] = [];
  for (const ticket of tickets) {
    if (statusOf(statusMap, ticket.id) === "done") continue;
    for (const dep of ticket.depends_on) {
      if (!known.has(dep)) {
        lines.push(`${ticket.id} phụ thuộc ${dep} không tồn tại`);
        continue;
      }
      const depStatus = statusOf(statusMap, dep);
      if (depStatus === "failed" || depStatus === "blocked") {
        lines.push(`${ticket.id} chờ ${dep} (${depStatus})`);
      }
    }
  }
  return lines;
}

// DFS 3 màu, duyệt riêng khỏi ticket-dag-validator.ts (N6): bỏ qua dep không
// tồn tại (đã có dòng riêng ở findBlockedByDependency) thay vì để nó che vòng.
function findCycles(tickets: Ticket[]): string[] {
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const state = new Map<string, "grey" | "black">();
  const stack: string[] = [];
  const cycles: string[] = [];
  const seenCycleKeys = new Set<string>();

  const walk = (id: string): void => {
    const mark = state.get(id);
    if (mark === "black") return;
    if (mark === "grey") {
      const cycle = [...stack.slice(stack.indexOf(id)), id];
      const key = [...new Set(cycle)].sort().join(",");
      if (!seenCycleKeys.has(key)) {
        seenCycleKeys.add(key);
        cycles.push(`vòng phụ thuộc: ${cycle.join(" → ")}`);
      }
      return;
    }
    state.set(id, "grey");
    stack.push(id);
    for (const dep of byId.get(id)?.depends_on ?? []) {
      if (!byId.has(dep)) continue; // dep không tồn tại: findBlockedByDependency lo việc này
      walk(dep);
    }
    stack.pop();
    state.set(id, "black");
  };

  for (const ticket of tickets) walk(ticket.id);
  return cycles;
}

/**
 * Lý do một mission `blocked` với DAG chưa xong nhưng không ticket nào sẵn
 * sàng dispatch. Thuần — trả mảng RỖNG khi không tìm được lý do nào (không
 * kẹt thật, hoặc caller gọi nhầm lúc không blocked).
 */
export function explainStall(
  tickets: Ticket[],
  statusMap: Record<string, TicketRuntimeStatus>,
): string[] {
  const lines = [...findBlockedByDependency(tickets, statusMap), ...findCycles(tickets)];
  return lines.slice(0, MAX_LINES);
}
